import { fullNodeById } from '../shared/array-types';
import { creatorSavedChoice } from '../janus-engine/character-creator/panels';
import { isUuid, itemsCol, layerEntriesOf, mergeNamesWithCatalog, nameOf, scalarLayerSlot } from './helpers';
import type { RenderBlob, RenderCtx } from './primitives';

export interface PropertyFormContext {
    source_id: string;
    instance_id?: string;
    choice_source_id?: string;
    exact_id?: string;
    form?: boolean;
    class_level?: number | null;
    choices?: Record<string, unknown>[];
}
export type PropertyChoiceRenderer = (layer: string, entries: unknown[], essenceClass?: string) => string;
type SectionRenderer = (ctx: RenderCtx, choice?: PropertyChoiceRenderer, context?: PropertyFormContext) => string;
type FormHost = Window & {
    ftMountPropertyFormSections?: (root?: ParentNode) => void;
    ftDefinitionsForDisplay?: (ids: string[]) => Promise<Record<string, Record<string, unknown> | null>>;
    ftHydrateUserChoiceOptions?: (root: HTMLElement) => void;
    initializeTomSelect?: () => void;
    calcEssences?: () => void;
    removeSelectUserChoices?: (layer: string) => void;
};
const host = window as FormHost;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const states = new WeakMap<HTMLElement, { key: string; pending: boolean }>();

/** The field names, family classes and saved-choice reader are shared with the creator. */
export function propertyChoiceRenderer(ctx: RenderCtx, context: PropertyFormContext): PropertyChoiceRenderer {
    return (layer, entries, essenceClass) => {
        const source = context.choice_source_id ?? context.source_id, exact = context.exact_id ?? '';
        const suffix = layer.replace(/^user_choice_\d+_/, '');
        const id = `${layer}-${source}_${exact}`, name = `${layer}[${source}_${exact}]`;
        const saved = creatorSavedChoice(context.choices ?? [], layer, source, exact);
        const attrs = ` id="${escapeHtml(id)}" name="${escapeHtml(name)}" data-ft-open-item-info-value="1"`
            + ` data-user-choice-layer="${escapeHtml(suffix)}" data-user-choice-selected="${escapeHtml(saved)}"`;
        const spec = entries.length === 1 ? scalarLayerSlot(entries[0]) : '';
        // c6choice15's public bridge owns spell specs. Keep the form identity on the
        // select so its async fill preserves the caller's name and delegated handlers.
        if (!essenceClass && spec && !isUuid(spec)) {
            return `<li class="list-group-item"><select class="select_user_choice spell_user_choice ${escapeHtml(suffix)}"${attrs}`
                + ` data-ft-uc-pending data-ft-uc-source="${escapeHtml(source)}" data-ft-uc-layer="${escapeHtml(layer)}"`
                + ` data-ft-uc-spec="${escapeHtml(spec)}" disabled><option value="">Loading choices…</option></select></li>`;
        }
        const options = spec === 'essence_increase'
            ? Object.entries(ESSENCE_CATALOG).filter(([key]) => ['body', 'mind', 'soul', 'fortune'].includes(key))
                .map(([, meta]) => ({ id: meta.item_id, name: '+1 ' + meta.item_name }))
            : entries.map(entry => {
                const id = scalarLayerSlot(entry);
                return { id, name: String(itemsCol(entry, 'item_name') ?? '') || nameOf(ctx.names, id) || id };
            }).filter(option => option.id !== '');
        if (saved && !options.some(option => upper(option.id) === upper(saved)))
            options.push({ id: saved, name: nameOf(ctx.names, saved) || `Saved choice (unavailable): ${saved}` });
        options.sort((a, b) => a.name.localeCompare(b.name));
        return `<li class="list-group-item"><select class="form-select mb-2 ts-select select_user_choice ${escapeHtml(essenceClass ?? suffix)}"${attrs}>`
            + '<option value="">--SELECT--</option>'
            + options.map(option => `<option value="${escapeHtml(option.id)}"${upper(option.id) === upper(saved) ? ' selected' : ''}>${escapeHtml(option.name)}</option>`).join('')
            + '</select></li>';
    };
}

/** Resolve identities with the existing page definition owner; never request a PHP card. */
export async function propertySectionContext(context: PropertyFormContext, section = ''): Promise<RenderCtx> {
    const id = upper(context.instance_id || context.source_id);
    if (!host.ftDefinitionsForDisplay) throw new Error('Property definitions unavailable. Please retry.');
    const definitions = await host.ftDefinitionsForDisplay([id]);
    const source = definitions[id];
    if (!source || upper(itemsCol(source, 'item_id')) !== upper(context.source_id))
        throw new Error(`Property definition unavailable: ${context.source_id}`);
    const arr = source as RenderBlob;
    // A definition closure already holds most refs. Explicit form choices may be
    // shells, so ask the same owner only for the missing choice definitions.
    const refs = Object.keys(arr.item_layer_types ?? {}).filter(key => context.form && (section === 'essences'
        ? /^user_choice_\d+_essence(?:_|$)/.test(key) : section === 'innate' && /^user_choice_\d+_innate_spell_per_long_rest$/.test(key)))
        .flatMap(key => layerEntriesOf(arr, key)).map(scalarLayerSlot).filter(isUuid);
    const fullById = fullNodeById(arr);
    const missing = refs.filter(ref => !fullById[upper(ref)]);
    const choices = missing.length ? await host.ftDefinitionsForDisplay(missing) : {};
    const catalog = { ...arr.catalog, ...Object.fromEntries(Object.entries(choices).filter((entry): entry is [string, Record<string, unknown>] => entry[1] !== null)) };
    const view = { ...arr, catalog };
    return { arr: view, names: mergeNamesWithCatalog({}, view), fullById: fullNodeById(view) };
}

export async function mountPropertyFormSection(el: HTMLElement, renderers: Readonly<Record<string, SectionRenderer>>): Promise<void> {
    const key = el.getAttribute('data-ft-property-context') ?? '';
    const section = el.getAttribute('data-ft-property-section') ?? '';
    const renderer = renderers[section];
    if (!key || !renderer) return;
    const previous = states.get(el), state = { key, pending: true };
    states.set(el, state);
    const context = JSON.parse(key) as PropertyFormContext;
    if (context.form && !el.querySelector('[data-ft-property-pending]')) {
        const pending = document.createElement('select');
        pending.className = 'select_user_choice'; pending.disabled = true; pending.hidden = true;
        pending.setAttribute('data-ft-property-pending', '1'); el.append(pending);
    }
    const current = (): boolean => el.isConnected && states.get(el) === state
        && el.getAttribute('data-ft-property-context') === key && el.getAttribute('data-ft-property-section') === section;
    try {
        const ctx = await propertySectionContext(context, section);
        if (!current()) return;
        // Read at paint time, so a slow refresh cannot roll back a live selection.
        const selections = new Map(previous?.key === key
            ? Array.from(el.querySelectorAll<HTMLSelectElement>('select[name]'), select => [select.name, select.value]) : []);
        const html = renderer(ctx, context.form ? propertyChoiceRenderer(ctx, context) : undefined, context);
        el.querySelectorAll<HTMLSelectElement>('select').forEach(select =>
            (select as HTMLSelectElement & { tomselect?: { destroy(): void } }).tomselect?.destroy());
        el.innerHTML = html;
        el.className = 'ft-ld-block';
        el.style.display = html ? 'block' : 'none';
        el.querySelectorAll<HTMLSelectElement>('select[name]').forEach(select => {
            if (!selections.has(select.name)) return;
            const value = selections.get(select.name)!;
            select.value = value;
            select.setAttribute('data-user-choice-selected', value);
        });
        el.setAttribute('data-ft-property-done', '1');
        host.ftHydrateUserChoiceOptions?.(el);
        host.initializeTomSelect?.();
        host.calcEssences?.();
    } catch (error) {
        if (!current()) return;
        el.removeAttribute('data-ft-property-done');
        // Existing selections survive a failed refresh. An initial failed read
        // must keep validation pending so Save cannot erase controls never loaded.
        if (el.querySelector('select[name]')) el.querySelector('[data-ft-property-pending]')?.remove();
        el.style.display = 'block';
        let message = el.querySelector<HTMLElement>('[data-ft-property-error]');
        if (!message) { message = document.createElement('div'); message.setAttribute('data-ft-property-error', '1'); el.append(message); }
        message.className = 'text-danger';
        message.textContent = error instanceof Error ? error.message : 'Property section unavailable. Please retry.';
    } finally { state.pending = false; }
}

/** Custom element connection covers direct HTML and AJAX insertions, without a document observer. */
export function installPropertyFormMounts(renderers: Readonly<Record<string, SectionRenderer>>): void {
    if (typeof customElements === 'undefined' || customElements.get('ft-property-section')) return;
    const mount = (el: HTMLElement): void => {
        const state = states.get(el), key = el.getAttribute('data-ft-property-context');
        if (!state || state.key !== key || (!state.pending && !el.hasAttribute('data-ft-property-done')))
            void mountPropertyFormSection(el, renderers);
    };
    host.ftMountPropertyFormSections = (root: ParentNode = document): void => {
        root.querySelectorAll<HTMLElement>('ft-property-section').forEach(mount);
        if (root instanceof HTMLElement && root.matches('ft-property-section')) mount(root);
    };
    customElements.define('ft-property-section', class extends HTMLElement {
        connectedCallback(): void {
            // The page owner registers later than early-render on direct page loads.
            if (document.readyState !== 'loading') mount(this);
        }
    });
    document.addEventListener('DOMContentLoaded', () => host.ftMountPropertyFormSections?.());
    document.addEventListener('change', event => {
        const select = event.target instanceof HTMLSelectElement ? event.target : null;
        if (!select?.closest('ft-property-section')) return;
        const essence = [...select.classList].some(value => /^(?:ancestral_|background_|feat_|ability_|inventory_)?essence_select$/.test(value));
        if (essence) host.calcEssences?.();
        else if (select.classList.contains('select_user_choice')) host.removeSelectUserChoices?.(select.getAttribute('data-user-choice-layer') ?? '');
    });
}
